import React, { FormEventHandler, useCallback, useReducer, useRef } from 'react';

import DropdownMenu from '../shared/dropdownMenu';
import Spinner from '../shared/spinner.component';
import { waitForSpinnerToShowUp } from '../shared/cursorHelpers';
import { generateAssetPath } from '../utils/assetsHelpers';
import FileUploadButton from '../shared/fileUpload/fileUploadButton.component';
import { acceptedExcelFileExtensions } from '../shared/fileUpload/constants';

type HamburgerDropdownParams = {
  canModify: boolean,
  disableEditing: boolean,
  exportToExcel?: (callback?: Function) => Promise<void> | void
  templatePath?: string,
  templateFileName?: string,
  fileToImportChange?: FormEventHandler<HTMLInputElement>,
  fileToImportSelect?: (callback?: Function) => () => Promise<void> | void
}

type HamburgerDropdownState = {
  isGeneratingTemplate?: boolean,
  isGeneratingExport?: boolean,
}
const initialState: HamburgerDropdownState = { isGeneratingTemplate: false, isGeneratingExport: false };

const HamburgerDropdown = ({
  canModify,
  disableEditing,
  exportToExcel,
  templatePath,
  templateFileName,
  fileToImportChange,
  fileToImportSelect,
}: HamburgerDropdownParams) => {
  const dropdownRef = useRef<DropdownMenu>(null);

  const [state, setState] = useReducer(
    (state: HamburgerDropdownState, newState: HamburgerDropdownState) => ({ ...state, ...newState }),
    initialState
  );

  const hideDropdown = useCallback(() => {
    dropdownRef.current?.hideDropDown();
  }, []);

  const onFileUploadButtonClick = useCallback(
    event => {
      fileToImportSelect?.(hideDropdown)();
      event.target.value = null;
    },
    [fileToImportSelect, hideDropdown]
  );

  const chooseFileToImportComponent = fileToImportChange && canModify
    ? (
      <FileUploadButton
        accept={acceptedExcelFileExtensions}
        className="upload-file-button hamburger-action"
        onChange={fileToImportChange}
        onClick={onFileUploadButtonClick}
        disabled={disableEditing}
      >
        Choose File to Import
      </FileUploadButton>
    )
    : null;

  const renderDownloadImportTemplate = templatePath && canModify
    ? (
      <a
        className="hamburger-action"
        role="button"
        tabIndex={1}
        download={templateFileName}
        href={generateAssetPath(templatePath)}
        onClick={hideDropdown}
      >
        Download Import Template
      </a>
    ) : null;

  const exportToExcelHandler = useCallback(
    () => {
      setState({ isGeneratingExport: true });
      waitForSpinnerToShowUp({
        action: exportToExcel,
        callback: () => setState({ isGeneratingExport: false }),
      });
    },
    [exportToExcel]
  );

  const exportToExcelComponent = exportToExcel
    ? (
      <button
        className="hamburger-action link-button"
        tabIndex={0}
        onClick={exportToExcelHandler}
      >
        {state.isGeneratingExport
          ? (
            <span>
              <Spinner small /> Exporting
            </span>
          ) : 'Export to Excel'
        }
      </button>
    )
    : null;

  return (
    <DropdownMenu ref={dropdownRef} contentClassName="hamburger-menu" >
      {exportToExcelComponent}
      {renderDownloadImportTemplate}
      {chooseFileToImportComponent}
    </DropdownMenu>
  );
};

export default HamburgerDropdown;
